// ManageSetsModal — picks which option sets become columns on a Data setup
// table. The sets themselves are authored in Data structure; this only decides
// which of them the table shows.
//
// Ticking is a LOCAL draft applied once by Done, the FilterDropdown idiom —
// the design's footer is a single Done with no Cancel, and drafting means the
// table doesn't reflow behind a modal the user can't see past. Closing by ✕ or
// the overlay discards, as it does there.
//
// The draft is re-seeded from the applied set on every open, so a discarded
// session leaves nothing behind. `enabled` must therefore be stable state on
// the caller, not a set minted inline each render, or this effect would never
// reach a fixed point (the PowerBiViewsModal lesson).
//
// Rows are the shared Checkbox with a ReactNode `label` carrying the title and
// the option count; `className` lands on Checkbox's root <label>, so the row
// keeps its own block name and stays click-to-toggle while the box, check glyph
// and focus ring remain the component's own. Row styling is scoped in the page
// partial rather than copying app-checkbox internals in here.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Modal } from '@/components/common';
import type { OptionSetSelection } from '@/api/endpoints/formSetsApi';

interface ManageSetsModalProps {
  open: boolean;
  onClose: () => void;
  sets: OptionSetSelection[];
  loading: boolean;
  error: boolean;
  /** The applied set ids. Must be stable state on the caller. */
  enabled: Set<string>;
  /** Fires once, on Done, with the set to apply. */
  onApply: (next: Set<string>) => void;
}

const ManageSetsModal = ({
  open,
  onClose,
  sets,
  loading,
  error,
  enabled,
  onApply,
}: ManageSetsModalProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Set<string>>(enabled);

  useEffect(() => {
    if (open) setDraft(new Set(enabled));
  }, [open, enabled]);

  const toggle = (id: string) =>
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const done = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='manage-sets'
      title={t('data_setup_sets_title')}
      subtitle={t('data_setup_sets_subtitle')}
      footer={<Button onClick={done}>{t('button_done')}</Button>}
    >
      <div className='manage-sets__list'>
        {/* A failed read is said so, never dressed up as "there are none" —
            those are different facts and Save-shaped mistakes follow from
            confusing them. */}
        {loading && <p className='manage-sets__empty'>{t('text_loading')}</p>}
        {!loading && error && <p className='manage-sets__empty'>{t('error_generic')}</p>}
        {!loading && !error && sets.length === 0 && (
          <p className='manage-sets__empty'>{t('data_setup_sets_empty')}</p>
        )}

        {!loading &&
          !error &&
          sets.map((s) => (
            <Checkbox
              key={s.id}
              className='manage-sets__row'
              checked={draft.has(s.id)}
              onChange={() => toggle(s.id)}
              label={
                <>
                  <span className='manage-sets__row-title'>{s.title}</span>
                  <span className='manage-sets__row-count'>
                    {t('data_setup_sets_options', { count: s.options.length })}
                  </span>
                </>
              }
            />
          ))}
      </div>
    </Modal>
  );
};

export default ManageSetsModal;
